import { type LocaleDefinition } from '@solinkcorp/locale';
import { getLocalTimeZone } from '@solinkcorp/utils';
import ThemeVariables from 'env/themeVariables';
import moment from 'moment';
import TimeFormatter from 'utils/TimeFormatter';
import { VIDEO_ASPECT_RATIO } from 'vd-constants';

export const diffTicks = (tick1: number, tick2: number, locale: LocaleDefinition) => {
  const diff = Math.floor((tick1 - tick2) / 1000) || 0;
  return moment.duration(diff, 'seconds').locale(locale.locale).humanize();
};

export const getLocalDate = (time: Date | number, tz: string) => {
  return moment.tz(time, tz).format('MMM Do YYYY');
};

export const getLocalTime = (time: Date | number, tz: string) => {
  return moment
    .tz(time, tz)
    .format(TimeFormatter.getString({ zeroPrefix: false, minutes: true, seconds: true }));
};

export const getTimezoneName = (timezoneId: string) => {
  return moment.tz(timezoneId).format('z');
};

export const isInMyTimezone = (timezoneId: string) => {
  const tz = getLocalTimeZone();

  const guessedTZ = moment.tz(tz).format('z');
  const playerTZ = moment.tz(timezoneId).format('z');

  return guessedTZ === playerTZ;
};

// withEvents = inEventPageContext || inClipsPageContext
// videoPage = inVideoPageContext
// embedded = compact chrome for in-app embeds (agent findings, etc.)
export type PlayerType =
  | 'withEvents'
  | 'videoPage'
  | 'embedded'
  | 'publicAlarm'
  | 'publicShare'
  | 'alarmVerification'
  | 'videoSearch';

export function isEmbeddedPlayerType(playerType: PlayerType): boolean {
  return playerType === 'embedded';
}

export const determineRoomForPlayerLayout = (
  windowSize: { width: number; height: number },
  availableSpace: { width: number; height: number },
  fullscreen: boolean,
  hideMiniMap: boolean,
  hideTitleBar = false,
  // Minimal-chrome mode (NDE video-first panel): the title bar and control
  // bar/timeline are drawn as auto-hiding overlays *on top of* the video rather
  // than in reserved rows around it, so no vertical margin is subtracted and the
  // video fills the whole box. Leave the band math untouched for every other
  // player, which still reserves rows for its chrome.
  overlayChrome = false,
) => {
  const { width, height } = fullscreen ? windowSize : availableSpace;

  let videoPlayerMargin = overlayChrome
    ? 0
    : ThemeVariables.videoPlayerOptionsHeight + ThemeVariables.videoMainChartHeight;

  if (!overlayChrome && hideTitleBar) {
    videoPlayerMargin -= ThemeVariables.videoPlayerOptionsHeight;
  }

  if (!overlayChrome && !hideMiniMap) {
    videoPlayerMargin += ThemeVariables.videoMiniMapHeight;
  }

  const maxPlayerHeight = height - videoPlayerMargin;

  const playerHeight = Math.min(width / VIDEO_ASPECT_RATIO, maxPlayerHeight);

  return {
    playerWidth: width,
    playerHeight,
    maxPlayerHeight,
  };
};
